/**
 * «Enviar»: el papel como PDF, no como texto (lo pidió el dueño, 2026-10-19:
 * «al darle en enviar, no envíe el texto, sino el pdf… en todos lados donde
 * esté el botón de enviar»).
 *
 *   · En el celular y la tablet se abre el menú de compartir del aparato
 *     (WhatsApp, correo, Telegram…) con el archivo adjunto.
 *   · En la computadora se descarga: WhatsApp Web y el correo no aparecen en
 *     el menú de compartir de Windows, y un archivo en Descargas se adjunta en
 *     cualquiera de los dos.
 *   · Si el aparato no sabe compartir archivos, o no deja, también se descarga:
 *     nunca se queda sin salida.
 */

import { aviso } from "@/stores/ui";

export type ResultadoDeEnviar = "compartido" | "descargado" | "cancelado";

/** «Cotización COT-000001» → «Cotización COT-000001.pdf», sin lo que un sistema no admite. */
function nombreDeArchivo(nombre: string): string {
  return `${nombre.replace(/[\\/:*?"<>|]+/g, "-").trim()}.pdf`;
}

function descargar(pdf: Blob, archivo: string): void {
  const url = URL.createObjectURL(pdf);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = archivo;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Tarde: soltarla enseguida puede cortar la descarga.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Un aparato de tocar (celular, tablet): ahí se comparte; en una computadora, se descarga. */
function esDeTocar(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * Manda el PDF: el menú de compartir en el celular, la descarga en la computadora.
 * `nombre` es lo que verá quien lo reciba: «Cotización COT-000001».
 */
export async function enviarPdf(pdf: Blob, nombre: string): Promise<ResultadoDeEnviar> {
  const archivo = nombreDeArchivo(nombre);
  if (esDeTocar() && typeof navigator.canShare === "function") {
    const adjunto = new File([pdf], archivo, { type: "application/pdf" });
    if (navigator.canShare({ files: [adjunto] })) {
      try {
        await navigator.share({ files: [adjunto], title: nombre });
        return "compartido";
      } catch (e) {
        // Cerró el menú sin elegir: no es un error.
        if (e instanceof DOMException && e.name === "AbortError") return "cancelado";
        // Cualquier otra cosa (el aparato no dejó): se descarga.
      }
    }
  }
  descargar(pdf, archivo);
  aviso.exito("PDF descargado", `«${archivo}» está en Descargas: adjúntelo en WhatsApp o en el correo.`);
  return "descargado";
}
